import { ref, onMounted, onUnmounted } from 'vue'
import { conflictResolutionService, type ConflictData, type ConflictResolution, type ConflictType } from '../services/ConflictResolutionService'
import type { Vehicle } from '../types/vehicle'
import type { Driver } from '../types/driver'
import type { ScheduleEntry } from '../types/schedule'

export function useConflictResolution() {
  const activeConflicts = ref<ConflictData[]>([])
  const currentConflict = ref<ConflictData | null>(null)
  const showConflictDialog = ref(false)
  const showConflictNotification = ref(false)
  const notificationMessage = ref('')

  let conflictUnsubscribe: (() => void) | null = null
  let resolutionUnsubscribe: (() => void) | null = null
  let autoRefreshListener: ((event: CustomEvent) => void) | null = null

  onMounted(() => {
    // Subscribe to conflict detection
    conflictUnsubscribe = conflictResolutionService.onConflictDetected((conflict) => {
      activeConflicts.value = conflictResolutionService.getAllConflicts()
      
      // Show notification
      notificationMessage.value = `A ${conflict.type} has been modified by another user.`
      showConflictNotification.value = true
      
      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        showConflictNotification.value = false
      }, 5000)
    })

    // Subscribe to conflict resolution
    resolutionUnsubscribe = conflictResolutionService.onConflictResolved((resolution) => {
      activeConflicts.value = conflictResolutionService.getAllConflicts()
      
      // Close dialog if it was for this conflict
      if (currentConflict.value?.id === resolution.conflictId) {
        showConflictDialog.value = false
        currentConflict.value = null
      }
    })

    // Listen for auto-refresh events
    autoRefreshListener = (event: CustomEvent) => {
      const { entityType, entityId } = event.detail
      handleAutoRefresh(entityType, entityId)
    }
    window.addEventListener('auto-refresh', autoRefreshListener as EventListener)

    // Load existing conflicts
    activeConflicts.value = conflictResolutionService.getAllConflicts()
  })

  onUnmounted(() => {
    if (conflictUnsubscribe) conflictUnsubscribe()
    if (resolutionUnsubscribe) resolutionUnsubscribe()
    if (autoRefreshListener) {
      window.removeEventListener('auto-refresh', autoRefreshListener as EventListener)
    }
  })

  const detectVehicleConflict = (localVehicle: Vehicle, remoteVehicle: Vehicle): ConflictData<Vehicle> | null => {
    const conflictedFields = conflictResolutionService.getConflictedFieldsForVehicle(localVehicle, remoteVehicle)
    
    if (conflictedFields.length === 0) return null

    return conflictResolutionService.detectConflict(
      localVehicle.id,
      'vehicle',
      localVehicle,
      remoteVehicle,
      conflictedFields
    )
  }

  const detectDriverConflict = (localDriver: Driver, remoteDriver: Driver): ConflictData<Driver> | null => {
    const conflictedFields = conflictResolutionService.getConflictedFieldsForDriver(localDriver, remoteDriver)
    
    if (conflictedFields.length === 0) return null

    return conflictResolutionService.detectConflict(
      localDriver.id,
      'driver',
      localDriver,
      remoteDriver,
      conflictedFields
    )
  }

  const detectScheduleConflict = (localSchedule: ScheduleEntry, remoteSchedule: ScheduleEntry): ConflictData<ScheduleEntry> | null => {
    const conflictedFields = conflictResolutionService.getConflictedFieldsForSchedule(localSchedule, remoteSchedule)
    
    if (conflictedFields.length === 0) return null

    return conflictResolutionService.detectConflict(
      localSchedule.id,
      'schedule',
      localSchedule,
      remoteSchedule,
      conflictedFields
    )
  }

  const showConflictResolutionDialog = (conflict: ConflictData) => {
    currentConflict.value = conflict
    showConflictDialog.value = true
    showConflictNotification.value = false
  }

  const resolveConflict = (resolution: ConflictResolution) => {
    conflictResolutionService.resolveConflict(resolution)
    
    // Emit custom event for components to handle the resolution
    window.dispatchEvent(new CustomEvent('conflict-resolved', {
      detail: resolution
    }))
  }

  const dismissConflictNotification = () => {
    showConflictNotification.value = false
  }

  const dismissConflictDialog = () => {
    showConflictDialog.value = false
    currentConflict.value = null
  }

  const handleAutoRefresh = (entityType: ConflictType, entityId: string) => {
    // Emit event for stores to refresh data
    window.dispatchEvent(new CustomEvent('entity-refresh-requested', {
      detail: { entityType, entityId }
    }))
  }

  const setOptimisticLock = (entityId: string, version: number, lastModified: Date) => {
    conflictResolutionService.setOptimisticLock(entityId, version, lastModified)
  }

  const checkOptimisticLock = (entityId: string, expectedVersion: number): boolean => {
    return conflictResolutionService.checkOptimisticLock(entityId, expectedVersion)
  }

  const removeOptimisticLock = (entityId: string) => {
    conflictResolutionService.removeOptimisticLock(entityId)
  }

  const clearAllConflicts = () => {
    conflictResolutionService.clearAllConflicts()
    activeConflicts.value = []
    showConflictDialog.value = false
    showConflictNotification.value = false
    currentConflict.value = null
  }

  const getConflictsByType = (type: ConflictType): ConflictData[] => {
    return conflictResolutionService.getConflictsByType(type)
  }

  return {
    // State
    activeConflicts,
    currentConflict,
    showConflictDialog,
    showConflictNotification,
    notificationMessage,

    // Methods
    detectVehicleConflict,
    detectDriverConflict,
    detectScheduleConflict,
    showConflictResolutionDialog,
    resolveConflict,
    dismissConflictNotification,
    dismissConflictDialog,
    setOptimisticLock,
    checkOptimisticLock,
    removeOptimisticLock,
    clearAllConflicts,
    getConflictsByType
  }
}